import { LicenseInvoiceDownloadButton } from "@/components/license-details/cards/license-invoice-download-button";
import {
  type LicensePaymentsDerived,
  type PaymentGroup,
  expandPaymentRowsForDisplay,
  formatPaymentGroupPeriodRangeLabel,
  isFeeAdjustmentLicensePayment,
  licensePaymentRowsSubtotalExcludingFeeAdjustments,
  parseLicenseMoneyField,
  paymentNetSubtotal,
  useLicensePaymentsDerived,
} from "@/components/license-details/cards/license-payments-derived";
import { CheckPaymentSuccessDialog } from "@/components/registrant/check-payment-success-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import { deleteManualPaymentMutation } from "@/hera-api/client/@tanstack/react-query.gen";
import { client } from "@/hera-api/client/client.gen";
import type {
  LicensePaymentResponseSchema,
  LicensePaymentsResponseSchema,
  NonRegistrantLicenseCommentResponseSchema,
  NonRegistrantLicensePaymentResponseSchema,
  NonRegistrantLicensePaymentsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { canDownloadInvoicePdf } from "@/lib/download-invoice-pdf";
import { notifyLicenseDetailsChanged } from "@/lib/license-details-change-notification";
import { type ReceiptScope, receiptScopeAccess } from "@/lib/receipt-scope";
import {
  downloadFileFromResponse,
  formatCurrency,
  formatDateNoTimezone,
  getQueryErrorMessage,
  licensePaymentRowHasBillableInvoiceFees,
  mapEffectivePaymentStatusToBadgeParams,
  mapManualTransactionStatusToBadgeParams,
} from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Pencil, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

export type EditableLicensePayment = {
  id: string;
  payment_type?: string | null;
  fee_paid: string;
  late_fee_paid: string;
  comment: string;
  is_comment_public: boolean;
  manual_payment_date: string | null;
  manual_payment_method_type?: string | null;
  note?: string | null;
  processing_fee?: string;
  period_start_date: string;
  period_end_date: string | null;
};

interface PaymentsCardContentProps {
  payments:
    | LicensePaymentsResponseSchema
    | NonRegistrantLicensePaymentsResponseSchema;
  userType: UserType;
  licenseId: string;
  licenseData?: {
    inspections?: Array<{
      id: string;
      effective_date?: string | null;
      effective_end_date?: string | null;
      created_at?: string;
      payments?: Array<{
        fee_paid: string;
        fee_prefilled: string;
        payment_type: string;
        payment_processor_transaction?: {
          status: string;
        } | null;
      }>;
    }>;
  };
  onPaymentDeleted?: (paymentId: string) => void;
  onPaymentEdit?: (payment: EditableLicensePayment) => void;
  /** When provided, avoids duplicating payment grouping / fees query (share with Current period summary card). */
  derived?: LicensePaymentsDerived;
  /** Nest delete confirm above an elevated parent dialog (e.g. case → license). */
  elevatedOverlays?: boolean;
}

export function PaymentsCardContent({
  payments,
  userType,
  licenseId,
  licenseData,
  onPaymentDeleted,
  onPaymentEdit,
  derived: derivedProp,
  elevatedOverlays = false,
}: PaymentsCardContentProps) {
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [checkPaymentInfo, setCheckPaymentInfo] = useState<{
    displayId: string;
  } | null>(null);
  const [pendingDeletePaymentId, setPendingDeletePaymentId] = useState<
    string | null
  >(null);

  const deletePayment = useMutation({
    ...deleteManualPaymentMutation(),
  });

  const {
    data: receiptTemplateGlobal,
    isLoading: isReceiptTemplateGlobalLoading,
  } = useQuery({
    queryKey: ["paymentReceiptTemplateAvailable", licenseId],
    queryFn: async () => {
      const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
      const auth = client.getConfig().auth;
      const url = `${baseUrl}/shared/licenses/${licenseId}/payment-receipt-template-available`;
      const doFetch =
        client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
      const res = await doFetch(
        new Request(url, {
          headers:
            typeof auth === "string" && auth
              ? { Authorization: `Bearer ${auth}` }
              : {},
        }),
      );
      if (!res.ok) {
        throw new Error(await res.text());
      }
      return res.json() as Promise<{ available: boolean }>;
    },
    enabled: isAuthSetupComplete && !!licenseId,
  });

  const hasPaymentReceiptTemplate = receiptTemplateGlobal?.available ?? false;

  const [receiptOpeningPaymentId, setReceiptOpeningPaymentId] = useState<
    string | null
  >(null);
  const [receiptScopePayment, setReceiptScopePayment] = useState<{
    id: string;
  } | null>(null);
  const { canChooseScope, defaultScope } = receiptScopeAccess(userType);

  const downloadReceipt = useCallback(
    async (
      payment: { id: string; _isInspectionPayment?: boolean },
      scope: ReceiptScope = "property",
    ) => {
      const paymentId = payment.id;
      const isInspectionPayment = payment._isInspectionPayment === true;
      setReceiptOpeningPaymentId(paymentId);
      try {
        const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
        const auth = client.getConfig().auth;
        const url = isInspectionPayment
          ? `${baseUrl}/shared/licenses/${licenseId}/inspection-payments/${paymentId}/receipt`
          : `${baseUrl}/shared/licenses/${licenseId}/payments/${paymentId}/receipt?scope=${scope}`;
        const doFetch =
          client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
        const response = await doFetch(
          new Request(url, {
            headers:
              typeof auth === "string" && auth
                ? { Authorization: `Bearer ${auth}` }
                : {},
          }),
        );
        if (!response.ok) {
          const text = await response.text();
          let detail = text;
          try {
            const parsed = JSON.parse(text) as { detail?: string | string[] };
            if (typeof parsed.detail === "string") {
              detail = parsed.detail;
            } else if (Array.isArray(parsed.detail)) {
              detail = parsed.detail.join(", ");
            }
          } catch {
            // use raw text
          }
          throw new Error(detail || response.statusText);
        }
        const blob = await response.blob();
        downloadFileFromResponse(
          blob,
          response.headers,
          isInspectionPayment
            ? `payment-receipt-inspection-${paymentId}.pdf`
            : `payment-receipt-${paymentId}.pdf`,
        );
      } catch (error) {
        toast({
          title: "Could not download receipt",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      } finally {
        setReceiptOpeningPaymentId(null);
      }
    },
    [licenseId],
  );

  const handleShowReceipt = useCallback(
    async (payment: { id: string; _isInspectionPayment?: boolean }) => {
      if (payment._isInspectionPayment === true) {
        await downloadReceipt(payment);
        return;
      }
      if (!canChooseScope) {
        await downloadReceipt(payment, defaultScope);
        return;
      }
      setReceiptOpeningPaymentId(payment.id);
      try {
        const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
        const auth = client.getConfig().auth;
        const doFetch =
          client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
        const response = await doFetch(
          new Request(
            `${baseUrl}/shared/licenses/${licenseId}/payments/${payment.id}/receipt-options`,
            {
              headers:
                typeof auth === "string" && auth
                  ? { Authorization: `Bearer ${auth}` }
                  : {},
            },
          ),
        );
        if (!response.ok) {
          throw new Error((await response.text()) || response.statusText);
        }
        const options = (await response.json()) as {
          crosses_multiple_properties: boolean;
          entire_payment_available: boolean;
        };
        if (
          options.crosses_multiple_properties &&
          options.entire_payment_available
        ) {
          setReceiptScopePayment({ id: payment.id });
          return;
        }
        await downloadReceipt(payment, defaultScope);
      } catch (error) {
        toast({
          title: "Could not download receipt",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      } finally {
        setReceiptOpeningPaymentId(null);
      }
    },
    [canChooseScope, defaultScope, downloadReceipt, licenseId],
  );

  const handleDeletePayment = async (paymentId: string) => {
    try {
      await deletePayment.mutateAsync({
        path: { license_id: licenseId, payment_id: paymentId },
      });

      await notifyLicenseDetailsChanged(queryClient, licenseId);
      toast({
        title: "Success",
        description: "Payment deleted successfully",
        variant: "secondary",
      });
      onPaymentDeleted?.(paymentId);
    } catch (error) {
      toast({
        title: "Error",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeletePayment = async () => {
    if (!pendingDeletePaymentId) return;
    await handleDeletePayment(pendingDeletePaymentId);
    setPendingDeletePaymentId(null);
  };

  const derivedInternal = useLicensePaymentsDerived({
    payments,
    userType,
    licenseId,
    licenseData,
  });
  const derived = derivedProp ?? derivedInternal;
  const { groupedLicensePayments, groupedInspectionPayments } = derived;

  // Helper function to render payment groups
  const renderPaymentGroups = (
    groupedPayments: Record<string, PaymentGroup>,
  ) => {
    if (Object.keys(groupedPayments).length === 0) {
      return <p className="text-sm text-muted-foreground">No payments found</p>;
    }

    return (
      <div className="space-y-0">
        {Object.keys(groupedPayments).map((periodGroupId) => {
          const paymentGroup = groupedPayments[periodGroupId];
          const periodSubtotal =
            licensePaymentRowsSubtotalExcludingFeeAdjustments(
              paymentGroup.payments,
            );
          const periodHeading = formatPaymentGroupPeriodRangeLabel(
            paymentGroup,
            {
              complianceSections: derived.periodComplianceSections,
              feesPayload: derived.renewalFees,
            },
          );

          return (
            <div
              key={periodGroupId}
              className="[&:not(:first-child)]:pt-4 pb-4"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-bold">{periodHeading}</h4>
                <div className="text-sm font-semibold">
                  Total: {formatCurrency(periodSubtotal)}
                </div>
              </div>
              <div className="space-y-2">
                {(() => {
                  const countablePayments = paymentGroup.payments.filter(
                    (p) => !isFeeAdjustmentLicensePayment(p),
                  );
                  const paymentOrdinalById = new Map(
                    countablePayments.map((p, i) => [p.id, i + 1]),
                  );
                  const totalCountablePayments = countablePayments.length;
                  return paymentGroup.payments
                    .flatMap((payment) => expandPaymentRowsForDisplay(payment))
                    .map((payment) => {
                      const isRefundLine =
                        (payment as { _isRefundLine?: boolean })
                          ._isRefundLine === true;
                      const isManualPayment =
                        payment.payment_type?.toLowerCase() ===
                        "manual_adjustment";
                      const isInspectionPayment =
                        (payment as any)._isInspectionPayment === true;
                      const isFeeAdjustment =
                        isFeeAdjustmentLicensePayment(payment);
                      const sourcePaymentId =
                        (payment as { _displayPaymentId?: string })
                          ._displayPaymentId ?? payment.id;
                      const paymentOrdinal =
                        paymentOrdinalById.get(sourcePaymentId);
                      const showPaymentIndex =
                        !isFeeAdjustment &&
                        !isRefundLine &&
                        paymentOrdinal !== undefined &&
                        totalCountablePayments > 1;

                      const grossPaid =
                        parseLicenseMoneyField(payment.fee_paid) +
                        parseLicenseMoneyField(payment.late_fee_paid);
                      const { status: paymentStatus, variant: paymentVariant } =
                        payment.payment_processor_transaction
                          ? mapEffectivePaymentStatusToBadgeParams(
                              (payment.payment_processor_transaction
                                .effective_status ??
                                payment.payment_processor_transaction
                                  .status) as Parameters<
                                typeof mapEffectivePaymentStatusToBadgeParams
                              >[0],
                              isRefundLine
                                ? undefined
                                : {
                                    netPaid: paymentNetSubtotal(payment),
                                    grossPaid: Math.abs(grossPaid),
                                  },
                            )
                          : payment.manual_transaction_status
                            ? mapManualTransactionStatusToBadgeParams(
                                payment.manual_transaction_status,
                                payment.manual_payment_method_type,
                              )
                            : {
                                status: "Manual",
                                variant: "secondary" as const,
                              };

                      const isUnpaidCheck =
                        isManualPayment &&
                        payment.manual_payment_method_type?.toLowerCase() ===
                          "check" &&
                        payment.manual_transaction_status?.toLowerCase() ===
                          "awaiting_payment";
                      const displayId = (
                        payment as NonRegistrantLicensePaymentResponseSchema
                      ).check_id;
                      const isFinalizedFeeAdjustment =
                        isFeeAdjustment &&
                        payment.manual_transaction_status?.toLowerCase() ===
                          "finalised";

                      return (
                        <div
                          key={`${payment.id}${isRefundLine ? "-refund" : ""}`}
                          className={`border rounded-lg p-3 space-y-2 ${
                            isUnpaidCheck && displayId
                              ? "cursor-pointer hover:bg-muted/50 transition-colors"
                              : ""
                          }`}
                          role={
                            isUnpaidCheck && displayId ? "button" : undefined
                          }
                          onClick={
                            isUnpaidCheck && displayId
                              ? () => setCheckPaymentInfo({ displayId })
                              : undefined
                          }
                          onKeyDown={
                            isUnpaidCheck && displayId
                              ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setCheckPaymentInfo({ displayId });
                                  }
                                }
                              : undefined
                          }
                        >
                          {/* Payment Header */}
                          <div className="flex justify-between items-start relative">
                            <div className="flex flex-wrap items-center gap-2">
                              {isFinalizedFeeAdjustment ? (
                                <>
                                  <Badge variant="default">
                                    Fee Adjustment
                                  </Badge>
                                  {displayId ? (
                                    <span className="text-xs font-medium text-muted-foreground">
                                      ({displayId})
                                    </span>
                                  ) : null}
                                </>
                              ) : isFeeAdjustment ? (
                                <>
                                  <Badge variant={paymentVariant}>
                                    {paymentStatus}
                                  </Badge>
                                  <Badge variant="default">
                                    Fee Adjustment
                                  </Badge>
                                  {displayId ? (
                                    <span className="text-xs font-medium text-muted-foreground">
                                      ({displayId})
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <Badge variant={paymentVariant}>
                                    {paymentStatus}
                                  </Badge>
                                  {showPaymentIndex ? (
                                    <span className="text-xs text-muted-foreground">
                                      Payment {paymentOrdinal} of{" "}
                                      {totalCountablePayments}
                                    </span>
                                  ) : null}
                                  <span className="text-xs font-medium">
                                    {isManualPayment
                                      ? (() => {
                                          const methodType =
                                            payment.manual_payment_method_type?.toLowerCase();
                                          const id = (
                                            payment as NonRegistrantLicensePaymentResponseSchema
                                          ).check_id;
                                          let label: string;
                                          if (methodType === "check") {
                                            label = "Check";
                                          } else if (methodType === "other") {
                                            label = "Other";
                                          } else if (methodType === "invoice") {
                                            label = "Invoice";
                                          } else {
                                            label = "Manual Payment";
                                          }
                                          return id
                                            ? `${label} (${id})`
                                            : label;
                                        })()
                                      : isInspectionPayment
                                        ? payment.payment_processor_transaction
                                            ?.was_imported
                                          ? "Imported Payment"
                                          : "Inspection Payment"
                                        : payment.payment_processor_transaction
                                              ?.was_imported
                                          ? "Imported Payment"
                                          : "Registrant Payment"}
                                  </span>
                                </>
                              )}
                            </div>
                            {!isRefundLine &&
                              !isInspectionPayment &&
                              userType === UserType.OPERATOR && (
                                <div className="flex gap-1 absolute top-0 right-0">
                                  <Button
                                    variant="ghost"
                                    className="hover:bg-secondary"
                                    size="icon"
                                    aria-label="Edit Payment..."
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onPaymentEdit?.({
                                        id: payment.id,
                                        payment_type: payment.payment_type,
                                        fee_paid: payment.fee_paid,
                                        late_fee_paid: payment.late_fee_paid,
                                        comment: payment.comment?.comment || "",
                                        is_comment_public: Boolean(
                                          (
                                            payment.comment as NonRegistrantLicenseCommentResponseSchema
                                          )?.is_public,
                                        ),
                                        manual_payment_date:
                                          payment.manual_payment_date,
                                        manual_payment_method_type:
                                          payment.manual_payment_method_type,
                                        note: payment.note,
                                        processing_fee: payment.processing_fee,
                                        period_start_date:
                                          payment.period_start_date,
                                        period_end_date:
                                          payment.period_end_date,
                                      });
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  {isManualPayment && (
                                    <Button
                                      variant="ghost"
                                      className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                                      size="icon"
                                      aria-label="Delete Payment..."
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingDeletePaymentId(payment.id);
                                      }}
                                      disabled={deletePayment.isPending}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                          </div>

                          {(
                            payment as {
                              source_license_display_id?: string | null;
                              obligation_registrant_name?: string | null;
                              paid_by_registrant_name?: string | null;
                              paid_in_full?: boolean;
                            }
                          ).source_license_display_id ||
                          (
                            payment as {
                              obligation_registrant_name?: string | null;
                            }
                          ).obligation_registrant_name ||
                          (
                            payment as {
                              paid_by_registrant_name?: string | null;
                            }
                          ).paid_by_registrant_name ? (
                            <div className="space-y-0.5 text-xs text-muted-foreground">
                              {(
                                payment as {
                                  source_license_display_id?: string | null;
                                }
                              ).source_license_display_id ? (
                                <p>
                                  From license{" "}
                                  {
                                    (
                                      payment as {
                                        source_license_display_id: string;
                                      }
                                    ).source_license_display_id
                                  }
                                </p>
                              ) : null}
                              {(
                                payment as {
                                  paid_by_registrant_name?: string | null;
                                }
                              ).paid_by_registrant_name ? (
                                <p>
                                  Paid by{" "}
                                  {
                                    (
                                      payment as {
                                        paid_by_registrant_name: string;
                                      }
                                    ).paid_by_registrant_name
                                  }
                                </p>
                              ) : !payment.paid_in_full &&
                                (
                                  payment as {
                                    obligation_registrant_name?: string | null;
                                  }
                                ).obligation_registrant_name ? (
                                <p>
                                  Obligation to pay{" "}
                                  {(userType === UserType.OPERATOR ||
                                    userType === UserType.GOVERNMENT) &&
                                  (
                                    payment as {
                                      source_license_display_id?: string | null;
                                    }
                                  ).source_license_display_id
                                    ? "transferred "
                                    : ""}
                                  {
                                    (
                                      payment as {
                                        obligation_registrant_name: string;
                                      }
                                    ).obligation_registrant_name
                                  }
                                </p>
                              ) : null}
                            </div>
                          ) : null}

                          {/* Payment Details */}
                          <div className="space-y-1">
                            {/* Timestamps */}
                            {isManualPayment ? (
                              <p className="text-xs text-muted-foreground">
                                {payment.manual_payment_date
                                  ? isFeeAdjustment
                                    ? `Adjustment Date: ${formatDateNoTimezone(payment.manual_payment_date)}`
                                    : `Payment Completed: ${formatDateNoTimezone(payment.manual_payment_date)}`
                                  : isFeeAdjustment
                                    ? `Adjustment recorded: ${new Date(payment.created_at).toLocaleString()}`
                                    : `Manually added: ${new Date(payment.created_at).toLocaleString()}`}
                              </p>
                            ) : (
                              <>
                                <p className="text-xs text-muted-foreground">
                                  {`Payment Initiated: ${new Date(payment.created_at).toLocaleString()}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {(() => {
                                    const tx =
                                      payment.payment_processor_transaction as
                                        | {
                                            succeeded_at?: string | null;
                                            payment_timestamp?: string | null;
                                          }
                                        | null
                                        | undefined;
                                    const completedAt =
                                      tx?.succeeded_at ?? tx?.payment_timestamp;
                                    return completedAt
                                      ? `Payment Completed: ${new Date(completedAt).toLocaleString()}`
                                      : "Payment completion date not available";
                                  })()}
                                </p>
                              </>
                            )}
                            {isManualPayment &&
                              payment.manual_payment_method_type?.toLowerCase() ===
                                "check" &&
                              payment.check_number?.trim() && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">
                                    Check number:{" "}
                                  </span>
                                  {payment.check_number}
                                </p>
                              )}
                            {payment.note?.trim() && (
                              <p className="text-xs text-muted-foreground">
                                <span className="font-medium">
                                  Payment note:{" "}
                                </span>
                                {payment.note}
                              </p>
                            )}

                            {/* Stripe Payment Intent - Show for operators and government users */}
                            {(userType === UserType.OPERATOR ||
                              userType === UserType.GOVERNMENT) &&
                              payment.payment_processor_transaction &&
                              (() => {
                                const transaction =
                                  payment.payment_processor_transaction as any;
                                return transaction.payment_intent_id;
                              })() && (
                                <p className="text-xs text-muted-foreground">
                                  <span className="font-medium">
                                    Stripe Payment Intent:{" "}
                                  </span>
                                  <a
                                    href={`https://dashboard.stripe.com/${((payment.payment_processor_transaction as any).payment_intent_id || "").startsWith("pi_test_") ? "test/" : ""}payments/${(payment.payment_processor_transaction as any).payment_intent_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline"
                                  >
                                    {
                                      (
                                        payment.payment_processor_transaction as any
                                      ).payment_intent_id
                                    }
                                  </a>
                                </p>
                              )}

                            {/* Fee Details */}
                            <div className="space-y-1 mt-4">
                              {isInspectionPayment ? (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium">
                                    Inspection Fee:
                                  </span>
                                  <span>
                                    {formatCurrency(
                                      Number.parseFloat(payment.fee_paid),
                                    )}{" "}
                                    {paymentStatus !== "Manual" &&
                                      payment.fee_paid !==
                                        payment.fee_prefilled &&
                                      `(prefill ${payment.fee_prefilled})`}
                                  </span>
                                </div>
                              ) : (
                                <>
                                  {isFeeAdjustment ? (
                                    <>
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="font-medium">
                                          Registration (adjustment):
                                        </span>
                                        <span>
                                          {formatCurrency(
                                            parseLicenseMoneyField(
                                              payment.fee_paid,
                                            ),
                                          )}{" "}
                                          {paymentStatus !== "Manual" &&
                                            payment.fee_paid !==
                                              payment.fee_prefilled &&
                                            `(prefill ${payment.fee_prefilled})`}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="font-medium">
                                          Late fee (adjustment):
                                        </span>
                                        <span>
                                          {formatCurrency(
                                            parseLicenseMoneyField(
                                              payment.late_fee_paid,
                                            ),
                                          )}{" "}
                                          {paymentStatus !== "Manual" &&
                                            payment.late_fee_paid !==
                                              payment.late_fee_prefilled &&
                                            `(prefill ${payment.late_fee_prefilled})`}
                                        </span>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="font-medium">
                                          Registration Fee:
                                        </span>
                                        <span>
                                          {formatCurrency(
                                            Number.parseFloat(payment.fee_paid),
                                          )}{" "}
                                          {paymentStatus !== "Manual" &&
                                            payment.fee_paid !==
                                              payment.fee_prefilled &&
                                            `(prefill ${payment.fee_prefilled})`}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center text-sm">
                                        <span className="font-medium">
                                          Late Fee:
                                        </span>
                                        <span>
                                          {formatCurrency(
                                            Number.parseFloat(
                                              payment.late_fee_paid,
                                            ),
                                          )}{" "}
                                          {paymentStatus !== "Manual" &&
                                            payment.late_fee_paid !==
                                              payment.late_fee_prefilled &&
                                            `(prefill ${payment.late_fee_prefilled})`}
                                        </span>
                                      </div>
                                    </>
                                  )}
                                </>
                              )}
                              {isManualPayment &&
                                Number.parseFloat(payment.processing_fee) >
                                  0 && (
                                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                                    <span className="font-medium">
                                      Processing Fee:
                                    </span>
                                    <span>
                                      {formatCurrency(
                                        Number.parseFloat(
                                          payment.processing_fee,
                                        ),
                                      )}{" "}
                                      <span className="text-xs">
                                        (not in total)
                                      </span>
                                    </span>
                                  </div>
                                )}
                              <div className="flex justify-between items-center text-sm font-semibold">
                                <span>
                                  {isFeeAdjustment
                                    ? "Adjustment total:"
                                    : "Payment Total:"}
                                </span>
                                <span>
                                  {formatCurrency(
                                    parseLicenseMoneyField(payment.fee_paid) +
                                      parseLicenseMoneyField(
                                        payment.late_fee_paid,
                                      ),
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Comment */}
                            {payment.comment?.comment?.trim() && (
                              <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                                <p className="font-medium text-xs text-muted-foreground mb-1">
                                  Comment:
                                </p>
                                <p>{payment.comment.comment.trim()}</p>
                              </div>
                            )}

                            {/* Download receipt PDF / invoice HTML when applicable */}
                            {(() => {
                              const isManualPayment =
                                payment.payment_type?.toLowerCase() ===
                                "manual_adjustment";
                              const isPaymentProcessor =
                                payment.payment_type?.toLowerCase() ===
                                "payment_processor";

                              const processorTransactionStatus =
                                payment.payment_processor_transaction?.status;
                              const effectiveProcessorStatus =
                                payment.payment_processor_transaction
                                  ?.effective_status ??
                                processorTransactionStatus;
                              const isPaidPaymentProcessor =
                                isPaymentProcessor &&
                                payment.payment_processor_transaction &&
                                (processorTransactionStatus === "succeeded" ||
                                  processorTransactionStatus ===
                                    "partially_refunded" ||
                                  processorTransactionStatus ===
                                    "dispute_won" ||
                                  effectiveProcessorStatus === "succeeded" ||
                                  effectiveProcessorStatus ===
                                    "partially_refunded" ||
                                  effectiveProcessorStatus === "dispute_won");

                              const isQualifiedManualPayment =
                                isManualPayment &&
                                (payment.manual_payment_method_type ===
                                  "check" ||
                                  payment.manual_payment_method_type ===
                                    "other");

                              const isQualifiedInvoiceManual =
                                isManualPayment &&
                                payment.manual_payment_method_type ===
                                  "invoice" &&
                                payment.manual_transaction_status ===
                                  "finalised";

                              const shouldShowReceiptButton =
                                hasPaymentReceiptTemplate &&
                                !isReceiptTemplateGlobalLoading &&
                                (isPaidPaymentProcessor ||
                                  isQualifiedManualPayment ||
                                  isQualifiedInvoiceManual);

                              const isDownloadingReceipt =
                                receiptOpeningPaymentId === payment.id;

                              const lp = payment as
                                | LicensePaymentResponseSchema
                                | NonRegistrantLicensePaymentResponseSchema;
                              const manualTxnId =
                                lp.manual_transaction_id ?? null;
                              const mtStatus = (
                                payment.manual_transaction_status ?? ""
                              ).toLowerCase();
                              const canDownloadInvoiceHtml =
                                isManualPayment &&
                                (
                                  payment.manual_payment_method_type ?? ""
                                ).toLowerCase() === "invoice" &&
                                !!manualTxnId &&
                                (mtStatus === "awaiting_payment" ||
                                  mtStatus === "finalised") &&
                                licensePaymentRowHasBillableInvoiceFees(lp);

                              const showDownloadInvoice =
                                canDownloadInvoicePdf(userType) &&
                                canDownloadInvoiceHtml;

                              if (
                                !shouldShowReceiptButton &&
                                !showDownloadInvoice
                              ) {
                                return null;
                              }

                              return (
                                <div className="mt-3 pt-3 border-t flex flex-col gap-2 sm:flex-row">
                                  {shouldShowReceiptButton && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleShowReceipt(
                                          payment as {
                                            id: string;
                                            _isInspectionPayment?: boolean;
                                          },
                                        )
                                      }
                                      disabled={
                                        isReceiptTemplateGlobalLoading ||
                                        isDownloadingReceipt
                                      }
                                      aria-busy={isDownloadingReceipt}
                                      className="w-full sm:flex-1"
                                    >
                                      {isDownloadingReceipt ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          {userType === UserType.REGISTRANT
                                            ? "Generating Receipt"
                                            : "Downloading Receipt..."}
                                        </>
                                      ) : (
                                        <>
                                          <FileText className="h-4 w-4 mr-2" />
                                          Download Receipt
                                        </>
                                      )}
                                    </Button>
                                  )}
                                  {showDownloadInvoice && manualTxnId && (
                                    <LicenseInvoiceDownloadButton
                                      userType={userType}
                                      manualTransactionId={manualTxnId}
                                      disabled={
                                        isReceiptTemplateGlobalLoading ||
                                        isDownloadingReceipt
                                      }
                                      className="w-full sm:flex-1"
                                    />
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <CardContent className="space-y-6">
      <AlertDialog
        open={receiptScopePayment != null}
        onOpenChange={(open) => {
          if (!open) setReceiptScopePayment(null);
        }}
      >
        <AlertDialogContent elevated={elevatedOverlays}>
          <AlertDialogHeader>
            <AlertDialogTitle>Download Receipt</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like a receipt for this property alone or for the entire
              payment made across multiple properties?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const payment = receiptScopePayment;
                setReceiptScopePayment(null);
                if (payment) void downloadReceipt(payment, "property");
              }}
            >
              This property alone
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                const payment = receiptScopePayment;
                setReceiptScopePayment(null);
                if (payment) void downloadReceipt(payment, "payment");
              }}
            >
              Entire payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={pendingDeletePaymentId != null}
        onOpenChange={(open) => {
          if (!open) setPendingDeletePaymentId(null);
        }}
      >
        <AlertDialogContent elevated={elevatedOverlays}>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this payment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePayment.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletePayment.isPending}
              onClick={handleConfirmDeletePayment}
            >
              {deletePayment.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CheckPaymentSuccessDialog
        isOpen={!!checkPaymentInfo}
        onOpenChange={(open) => {
          if (!open) setCheckPaymentInfo(null);
        }}
        displayId={checkPaymentInfo?.displayId ?? ""}
      />
      {/* Registration Payments Section */}
      <div>
        <h3 className="font-semibold text-base mb-4">Registration Payments</h3>
        {renderPaymentGroups(groupedLicensePayments)}
      </div>

      {/* Inspection Payments Section */}
      {Object.keys(groupedInspectionPayments).length > 0 && (
        <div className="pt-4 border-t">
          <h3 className="font-semibold text-base mb-4">Inspection Payments</h3>
          {renderPaymentGroups(groupedInspectionPayments)}
        </div>
      )}
    </CardContent>
  );
}
